import { IconNames } from '@dixa/lib-shared-habla';
import type { ConversationContentType } from 'lib-shared-types-api';
import type { Channel, Media } from 'lib-shared-types-channels';

import { SanitizationProfileType } from 'utils/sanitizeHtml';

/** The mapping from media to channels. */
export const mediaToChannels: Readonly<Record<Media, readonly Channel[]>> = {
  email: ['email', 'contact_form'],
  interactivechat: [
    'widget_chat',
    'messenger',
    'adachat',
    'digitalgeniuschat',
    'kindlychat',
    'trustpilotreviews',
  ],
  speak: ['voicemail', 'callback', 'pstn_phone'],
  messaging: ['facebook_messenger', 'whats_app', 'sms', 'twitter'],
};

/**
 * Represents a map of explicit channel names (as defined in agent-interface) to
 * managed_channel (as it is defined in presence service)
 */
export const managedChannelMap: Partial<Record<Channel, 'managed_channel'>> = {
  adachat: 'managed_channel',
  digitalgeniuschat: 'managed_channel',
  kindlychat: 'managed_channel',
  trustpilotreviews: 'managed_channel',
};

/**
 * Represents various channel specific attributes and configurations.
 *
 * @remarks
 *   In some features, this map is used to render channel items in selection
 *   lists, therefore the order of the channels in the list is important, as
 *   they are grouped and ordered in a certain way.
 */
export const channelAttributes: Readonly<
  Record<
    Channel,
    {
      /** The icon of the channel. */
      readonly icon: IconNames;
      /** The pretty name of the channel. */
      readonly name: string;
      /**
       * The content type.
       *
       * This is the format in which messages are sent and received to/from the
       * backend for the channel. A value of `text` can be either plaintext or
       * markdown.
       */
      readonly contentType?: ConversationContentType;
      /** The editor configuration for textual based channels. */
      readonly editor?: {
        /** The sanitization profile. */
        readonly sanitizationProfile: SanitizationProfileType;
        /** Whether to allow inline attachments. */
        readonly allowInlineAttachments?: boolean;
        /** Whether to allow attachments. */
        readonly allowAttachments?: boolean;
        /** Whether to show the hide send on enter toggle button. */
        readonly hideSendOnEnterToggle?: boolean;
      };
    }
  >
> = {
  messenger: {
    icon: 'messenger',
    name: 'Messenger',
    contentType: 'text',
    editor: {
      sanitizationProfile: 'email',
    },
  },
  widget_chat: {
    icon: 'chat',
    name: 'Chat',
    contentType: 'text',
    editor: {
      sanitizationProfile: 'widgetChat',
    },
  },
  email: {
    icon: 'mail',
    name: 'Email',
    contentType: 'html',
    editor: {
      sanitizationProfile: 'email',
    },
  },
  contact_form: {
    icon: 'chat-widget',
    name: 'Contact Form',
    contentType: 'html',
    editor: {
      sanitizationProfile: 'email',
    },
  },
  pstn_phone: { icon: 'phone', name: 'Phone' },
  voicemail: { icon: 'voicemail', name: 'Voicemail' },
  callback: { icon: 'phone-plus', name: 'Callback' },
  facebook_messenger: {
    icon: 'facebook-messenger',
    name: 'Facebook Messenger',
    contentType: 'text',
    editor: {
      sanitizationProfile: 'plainText',
    },
  },
  whats_app: {
    icon: 'whatsapp',
    name: 'WhatsApp',
    contentType: 'text',
    editor: {
      sanitizationProfile: 'whatsApp',
    },
  },
  sms: {
    icon: 'sms',
    name: 'Text',
    contentType: 'text',
    editor: {
      sanitizationProfile: 'plainText',
    },
  },
  twitter: {
    icon: 'twitter',
    name: 'Twitter',
    contentType: 'text',
    editor: {
      sanitizationProfile: 'plainText',
    },
  },
  adachat: {
    icon: 'ada',
    name: 'Ada Chat',
    contentType: 'text',
    editor: {
      sanitizationProfile: 'email',
    },
  },
  digitalgeniuschat: {
    icon: 'digitalgenius',
    name: 'DigitalGenius Chat',
    contentType: 'text',
    editor: {
      sanitizationProfile: 'email',
    },
  },
  kindlychat: {
    icon: 'kindly',
    name: 'Kindly Chat',
    contentType: 'text',
    editor: {
      sanitizationProfile: 'email',
    },
  },
  trustpilotreviews: {
    icon: 'trustpilot-outlined',
    name: 'Trustpilot',
    contentType: 'text',
    editor: {
      sanitizationProfile: 'email',
    },
  },
};

/**
 * Gets the media of a channel.
 *
 * @param channel The channel.
 */
export function getChannelMedia(channel: Channel): Media {
  const channelMedia = Object.keys(mediaToChannels).find((media) =>
    mediaToChannels[media as Media].includes(channel),
  ) as Media | undefined;

  if (channelMedia) return channelMedia;

  throw Error(
    `Media not found, a channel should always belong to a media. (${channel})`,
  );
}

/**
 * Gets the readable name of a channel.
 *
 * @param channel The channel.
 * @returns The readable name of the channel.
 */
export function getChannelName(channel: Channel): string {
  return channelAttributes[channel].name;
}
